import {
  Dropdown,
  Icon,
  IconButton,
  Image,
  Menu,
  MenuItem,
  Stack,
  Tag,
  TextBody,
  TextTitle,
  logos,
} from '@pypestream/design-system';
import { FC } from 'react';

import { capitalizeWords, getEnvDotStyle } from '../../utils';

const KEBAB_TRIGGER_ID = 'ps-channel-settings-header-actions';

/**
 * The Channel Settings page header (PSTUDIO-3768): the channel's own name as the
 * page title with its environment beside it, the Pype product mark on the right,
 * and a kebab for the actions that used to be fields.
 *
 * Renaming lives behind that kebab because the Channel Details card it came from
 * is create-only now — on an existing channel the environment can't be changed
 * at all, so a whole card for one editable field was mostly read-only chrome.
 */
export const ChannelSettingsHeader: FC<{
  name: string;
  /** Raw channel env, e.g. `live` / `sandbox`. */
  env: string;
  onRename: () => void;
  /** Viewers get the title without the rename affordance. */
  isViewer: boolean;
}> = ({ name, env, onRename, isViewer }) => (
  <Stack
    test-id="ps-channel-settings-header"
    justifyContent="space-between"
    alignItems="center"
    gutter="small"
  >
    <Stack alignItems="center" gutter="small" nowrap>
      <TextTitle test-id="ps-channel-settings-title" size="small">
        {name}
      </TextTitle>
      {/* `success` is the light-green env pill already used for live elsewhere
          (project configurations, the channels table); sandbox stays neutral. */}
      <Tag
        test-id="ps-channel-settings-env-tag"
        size="small"
        minimal
        variant={env.toLowerCase() === 'live' ? 'success' : 'default'}
      >
        <Stack alignItems="center" gutter="2xsmall" nowrap>
          <span style={getEnvDotStyle(env, true)} />
          {capitalizeWords(env)}
        </Stack>
      </Tag>
    </Stack>

    <Stack alignItems="center" gutter="small" nowrap>
      <Stack
        test-id="ps-channel-settings-header-pype-logo"
        alignItems="center"
        gutter="xsmall"
        nowrap
      >
        {/* The Pype product mark from the design system's logo set — the same
            asset `getPlatformLogo` serves, not the Pypestream wordmark. */}
        <Image width="24" height="24" src={logos.pype} />
        <TextBody>Pype</TextBody>
      </Stack>
      {!isViewer && (
        <>
          <IconButton
            test-id="ps-channel-settings-header-kebab"
            name="kebab"
            size="small"
            id={KEBAB_TRIGGER_ID}
          />
          <Dropdown trigger={KEBAB_TRIGGER_ID} placement="bottom-end">
            <Menu>
              <MenuItem
                test-id="ps-channel-settings-header-rename"
                onClick={onRename}
              >
                <Icon name="edit" size="small" slot="prefix" />
                Edit
              </MenuItem>
            </Menu>
          </Dropdown>
        </>
      )}
    </Stack>
  </Stack>
);
